<template>
  <div>
    <b-input-group class="mb-1">
      <b-form-input
        v-model="input_search"
        type="search"
        size="sm"
        :placeholder="`${$t('reusable.common.search')} ${entityNamePlural}...`"
      ></b-form-input>
      <b-input-group-append>
        <b-button
          size="sm"
          :disabled="!input_search"
          @click="input_search = ''"
        >
          {{ $t("reusable.common.clear") }}
        </b-button>
      </b-input-group-append>
    </b-input-group>

    <!-- Mismo aviso que el selector del Programa: las minimas de la Asignatura van
         coloreadas. Se reusan sus claves para que las dos vistas digan lo mismo. -->
    <small v-if="matterStrategies.length > 0" class="ml-1">
      {{ $t("matterProgram.matterProgramView.coloredMinimumEntitiesStart") }}
      <span style="color: var(--kl-thirth-hover-color)">
        {{ $t("matterProgram.matterProgramView.coloredMinimumEntitiesColored") }}
      </span>
      {{
        $t("matterProgram.matterProgramView.coloredMinimumEntitiesEnd", {
          entity1: entityNamePlural,
          entity2: $getVisibleNames(
            "mesh.egressprofilematter",
            false,
            "Asignatura"
          ),
        })
      }}.
    </small>

    <p v-if="catalogue.length === 0" class="text-muted small mb-0 py-2">
      {{
        $t("teaching.instructional.activityStrategiesEmpty", {
          entity: strategyEntityNamePlural,
        })
      }}
    </p>

    <b-form-select
      v-else
      :value="selected_ids"
      @change="onStrategiesChange"
      size="sm"
      multiple
      :select-size="selectSize"
    >
      <b-form-select-option value="__clear__">
        — {{ $t("reusable.common.clear") }} —
      </b-form-select-option>
      <b-form-select-option
        v-for="strategy in matterStrategies"
        :key="`matter-strategy-${strategy.id}`"
        :value="strategy.id"
        style="background-color: var(--kl-thirth-hover-color)"
        :title="optionTitle(strategy)"
      >
        {{ strategy.name }}
      </b-form-select-option>
      <b-form-select-option
        v-for="strategy in searchStrategies"
        :key="`strategy-${strategy.id}`"
        :value="strategy.id"
        :title="optionTitle(strategy)"
      >
        {{ strategy.name }}
      </b-form-select-option>
    </b-form-select>

    <!-- Crear una estrategia. Va en su propio modal y no aca abajo: el formulario
         pide nombre y descripcion, y meterlo debajo del selector empujaba la lista
         fuera de la pantalla justo cuando el usuario esta eligiendo. -->
    <div v-if="can_create" class="mt-2">
      <ButtonMedium
        :text_button="$t('reusable.common.createTooltip', { entity: entityName })"
        :click_button="openCreateModal"
        :icon="'plus'"
        :variant="'primary'"
      ></ButtonMedium>
    </div>

    <div class="text-right mt-3">
      <b-button size="sm" class="mr-1 btn-cancel" @click="$emit('cancel')">
        {{ $t("reusable.common.cancel") }}
      </b-button>
      <b-button size="sm" @click="$emit('saved', [...selected_ids])">
        {{ $t("reusable.common.save") }}
      </b-button>
    </div>

    <b-modal
      :id="createModalId"
      :title="$t('reusable.common.createTooltip', { entity: entityName })"
      size="md"
      hide-footer
      ignore-enforce-focus-selector="#sidebar-right"
      @show="resetNewStrategy"
    >
      <b-form-group
        :label="`${$t('reusable.common.name')}:`"
        label-for="input-new-strategy-name"
        label-cols="4"
        label-cols-sm="3"
        class="label"
      >
        <b-form-input
          id="input-new-strategy-name"
          v-model="new_strategy.name"
          size="sm"
          :state="new_name_state"
        ></b-form-input>
        <b-form-invalid-feedback :state="new_name_state">
          {{ $t("reusable.common.minLength", { min: 5 }) }}
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group
        :label="`${$t('reusable.common.description')}:`"
        label-for="input-new-strategy-description"
        label-cols="4"
        label-cols-sm="3"
        class="label"
      >
        <b-form-textarea
          id="input-new-strategy-description"
          v-model="new_strategy.description"
          size="sm"
          rows="3"
        ></b-form-textarea>
      </b-form-group>

      <!-- Lo que se crea aca NO es de esta ficha: entra al catalogo de la
           institucion. Se dice a la vista, no en la documentacion. -->
      <p class="kl-strategy-warning">
        <b-icon icon="exclamation-triangle" scale="0.85" class="mr-1"></b-icon>
        {{ $t("teaching.instructional.activityStrategiesCreateWarning") }}
      </p>

      <div class="text-right">
        <b-button
          size="sm"
          class="mr-1 btn-cancel"
          @click="$bvModal.hide(createModalId)"
        >
          {{ $t("reusable.common.cancel") }}
        </b-button>
        <b-button
          size="sm"
          :disabled="new_name_state !== true || creating_strategy"
          @click="createStrategy"
        >
          {{ $t("reusable.common.create") }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import * as names from "@/store/names";
import { mapGetters } from "vuex";
import { toast } from "@/utils/utils";

// Alto maximo del selector, en filas. Las otras vistas lo dejan crecer con el
// catalogo entero; aca se acota porque el selector vive dentro de un modal que ya
// esta dentro del modal de la actividad, y con 60 estrategias los botones de
// Guardar quedaban fuera de la pantalla.
const MAX_SELECT_ROWS = 12;
// La descripcion viaja en el `title` de la opcion. Puede tener miles de caracteres
// (hay una de 3.633), asi que se recorta: un tooltip nativo de ese largo tapa la
// pantalla y no se puede cerrar.
const TOOLTIP_LENGTH = 180;

export default {
  name: "ActivityMethodologicalStrategies",
  props: {
    // Ids ya elegidos. Se copian: la seleccion se confirma con Guardar, y ese Guardar solo
    // actualiza el formulario que monta este componente — lo que persiste es el guardado de
    // ese formulario, hoy el del Programa de Asignatura.
    selected: {
      type: Array,
      default: () => [],
    },
    // Las que la Asignatura declaro como minimas, para mostrarlas coloreadas.
    matter_strategy_ids: {
      type: Array,
      default: () => [],
    },
    can_create: {
      type: Boolean,
      default: false,
    },
    // Distingue los modales cuando hay varias fichas montadas a la vez.
    unique_id: {
      type: [Number, String],
      default: "new",
    },
  },
  components: {
    ButtonMedium: () =>
      import("@/components/reusable/Buttons/Medium/ButtonMedium"),
  },
  data() {
    return {
      selected_ids: [...this.selected],
      input_search: "",
      creating_strategy: false,
      new_strategy: { name: "", description: "" },
    };
  },
  computed: {
    strategyEntityNamePlural() {
      return this.$getVisibleNames(
        "mesh.methodologicalstrategy",
        true,
        "Estrategias Metodológicas"
      );
    },
    ...mapGetters({
      methodologicalStrategies: names.METHODOLOGICAL_STRATEGIES,
      institution: "getInstitution",
    }),
    entityName() {
      return this.$getVisibleNames(
        "mesh.methodologicalstrategy",
        false,
        "Estrategia Metodológica"
      );
    },
    entityNamePlural() {
      return this.$getVisibleNames(
        "mesh.methodologicalstrategy",
        true,
        "Estrategias Metodológicas"
      );
    },
    createModalId() {
      return `instructional-strategy-create-modal-${this.unique_id}`;
    },
    // Una estrategia dada de baja sigue listada si la ficha ya la tenia: si no, se
    // perderia sola al guardar sin que nadie lo note.
    catalogue() {
      return this.methodologicalStrategies.filter(
        (x) => x.is_active || this.selected_ids.includes(x.id)
      );
    },
    // Las minimas de la Asignatura van primero y coloreadas, como en el Programa.
    matterStrategies() {
      return this.catalogue
        .filter((x) => this.matter_strategy_ids.includes(x.id))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    otherStrategies() {
      return this.catalogue
        .filter((x) => !this.matter_strategy_ids.includes(x.id))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    // La busqueda nunca esconde lo ya elegido: si no, el usuario lo perderia de
    // vista y creeria que se desmarco.
    searchStrategies() {
      const search = this.input_search.toLowerCase();
      if (!search) return this.otherStrategies;
      return this.otherStrategies.filter(
        (x) =>
          x.name.toLowerCase().includes(search) ||
          this.selected_ids.includes(x.id)
      );
    },
    selectSize() {
      const total =
        this.matterStrategies.length + this.searchStrategies.length + 1;
      return total > MAX_SELECT_ROWS ? MAX_SELECT_ROWS : total;
    },
    new_name_state() {
      if (this.new_strategy.name.length === 0) return null;
      return this.new_strategy.name.trim().length >= 5;
    },
  },
  methods: {
    // "— Limpiar —" es una opcion mas del multiple: si viene en la seleccion, lo
    // que el usuario quiso fue vaciar la lista.
    onStrategiesChange(value) {
      this.selected_ids = value.includes("__clear__") ? [] : value;
    },
    // La descripcion se guarda como HTML y puede tener miles de caracteres: al
    // tooltip va en texto plano y recortada.
    optionTitle(strategy) {
      const plain = this.$htmlToPlainText(strategy.description || "").trim();
      const description =
        plain.length > TOOLTIP_LENGTH
          ? `${plain.slice(0, TOOLTIP_LENGTH).trim()}…`
          : plain;
      if (!this.matter_strategy_ids.includes(strategy.id))
        return description || strategy.name;
      const minimum = this.$t("teaching.instructional.activityStrategiesMatter", {
        entity: this.$getVisibleNames(
          "mesh.egressprofilematter",
          false,
          "Asignatura"
        ),
      });
      return description ? `${minimum}\n${description}` : minimum;
    },
    resetNewStrategy() {
      this.new_strategy = { name: "", description: "" };
    },
    openCreateModal() {
      this.$bvModal.show(this.createModalId);
    },
    // La estrategia se crea de inmediato en el catalogo (es un objeto de la
    // institucion, no de la ficha) y queda marcada. La ficha recien la guarda
    // cuando el usuario guarde el formulario.
    createStrategy() {
      if (this.new_name_state !== true) return;
      this.creating_strategy = true;
      this.$store
        .dispatch(names.POST_METHODOLOGICAL_STRATEGY, {
          name: this.new_strategy.name.trim(),
          description: this.new_strategy.description,
          is_active: true,
          school: this.institution ? this.institution.id : null,
        })
        .then((response) => {
          this.creating_strategy = false;
          this.selected_ids = [...this.selected_ids, response.id];
          this.input_search = "";
          this.$bvModal.hide(this.createModalId);
          // Misma forma que usa el modulo de Estrategias: la clave es un fragmento
          // ("creada."), no una plantilla con entidad.
          toast(`${this.entityName} ${this.$t("reusable.common.created")}`);
        })
        .catch(() => {
          this.creating_strategy = false;
          toast(this.$t("teaching.instructional.saveError"));
        });
    },
  },
};
</script>

<style scoped>
/* Lo que se crea aca no es de esta ficha: entra al catalogo de la institucion. */
.kl-strategy-warning {
  font-size: var(--kl-fs-small);
  color: #8a6d3b;
  margin-bottom: 0.75rem;
}
.btn-cancel {
  margin-right: auto;
}
</style>
